/**
 * Report Template Manager
 * HTML template'lerini yönetir ve veri ile doldurur
 */

class ReportTemplateManager {
    constructor() {
        this.templates = new Map();
        this.templatesLoaded = false;
        this.loadTemplates();
    }

    /**
     * Template'leri yükler
     */
    async loadTemplates() {
        try {
            
            // Stop Sale Report Template - Public klasöründen yükle
            const response = await fetch('/reports/salesandmarketing/StopSaleReport.html');
            
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const stopSaleTemplate = await response.text();
            
            this.templates.set('stopSale', stopSaleTemplate);
            this.templatesLoaded = true;
        } catch (error) {
            // Fallback: Basit template
            const fallbackTemplate = `
<!DOCTYPE html>
<html>
<head>
    <title>Open & Stop Sale Report</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .info-table { width: 100%; border-collapse: collapse; }
        .info-table th, .info-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        .info-table th { background-color: #f2f2f2; }
    </style>
</head>
<body>
    <div class="header">
        <h1>AKKA Hotels</h1>
        <h2>Open & Stop Sale Report</h2>
    </div>
    <table class="info-table">
        <tr><th>Hotel</th><td>{{hotel_name}}</td></tr>
        <tr><th>Room Type</th><td>{{roomtype_name}}</td></tr>
        <tr><th>Type</th><td>{{openstoptype_name}}</td></tr>
        <tr><th>Start Date</th><td>{{start_date}}</td></tr>
        <tr><th>Finish Date</th><td>{{finish_date}}</td></tr>
        <tr><th>Market</th><td>{{market_name}}</td></tr>
        <tr><th>Status</th><td>{{status_text}}</td></tr>
    </table>
    <div style="margin-top: 20px; text-align: center; font-size: 12px; color: #666;">
        Generated on: {{generated_date}} at {{generated_time}}
    </div>
</body>
</html>`;
            
            this.templates.set('stopSale', fallbackTemplate);
            this.templatesLoaded = true;
        }
    }

    /**
     * Template'i veri ile doldurur
     * @param {string} templateName - Template adı
     * @param {Object} data - Doldurulacak veri
     * @returns {string} Doldurulmuş HTML
     */
    fillTemplate(templateName, data) {
        const template = this.templates.get(templateName);
        if (!template) {
            throw new Error(`Template bulunamadı: ${templateName}`);
        }

        let filledTemplate = template;

        // Veri alanlarını template'e doldur
        Object.keys(data).forEach(key => {
            const placeholder = `{{${key}}}`;
            const value = data[key] || '-';
            filledTemplate = filledTemplate.replace(new RegExp(placeholder, 'g'), value);
        });

        return filledTemplate;
    }

    /**
     * Otel adına göre logo URL'ini döndürür
     * @param {string} hotelName - Otel adı
     * @returns {string} Logo URL'i
     */
    getHotelLogo(hotelName) {
        if (!hotelName) {
            return '/antedon.png'; // Varsayılan logo Antedon olarak değiştirildi
        }
        
        const hotelNameLower = hotelName.toLowerCase();
        
        // Otel adına göre logo eşleştirmesi
        if (hotelNameLower.includes('antedon')) {
            return '/antedon.png';
        } else if (hotelNameLower.includes('claros')) {
            return '/claros.png';
        } else if (hotelNameLower.includes('alinda')) {
            return '/alinda.png';
        } else if (hotelNameLower.includes('lush')) {
            return '/lush.png';
        } else if (hotelNameLower.includes('suites')) {
            return '/suites.png';
        } else if (hotelNameLower.includes('residence')) {
            return '/antedon.png'; // Residence için Antedon logosu
        } else {
            // Varsayılan logo Antedon
            return '/antedon.png';
        }
    }

    /**
     * Stop Sale raporu için veri hazırlar
     * @param {Object} entry - Stop sale kaydı
     * @returns {Object} Rapor verisi
     */
    prepareStopSaleData(entry) {
        const now = new Date();
        const generatedDate = now.toLocaleDateString('tr-TR');
        const generatedTime = now.toLocaleTimeString('tr-TR');
        
        // Otel adına göre dinamik logo seçimi
        const logoUrl = this.getHotelLogo(entry.hotel_name);
        
        return {
            hotel_name: entry.hotel_name || '-',
            roomtype_name: entry.roomtype_name || '-',
            openstoptype_name: entry.openstoptype_name || '-',
            date_type_name: entry.date_type_name || '-',
            start_date: entry.start_date || '-',
            finish_date: entry.finish_date || '-',
            market_name: entry.market_name || '-',
            operator_name: entry.operator_name || '-',
            maleonly_name: entry.maleonly_name || '-',
            status_text: entry.is_sent ? 'Sent' : 'Pending',
            status_class: entry.is_sent ? 'status-sent' : 'status-pending',
            generated_date: generatedDate,
            generated_time: generatedTime,
            report_id: `OSS-${entry.id}-${Date.now()}`,
            logo_url: logoUrl
        };
    }

    /**
     * Raporu yazdırır
     * @param {string} templateName - Template adı
     * @param {Object} data - Rapor verisi
     */
    async printReport(templateName, data) {
        try {
            
            // Template'ler yüklenmemişse bekle
            if (!this.templatesLoaded) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
            // Template'i doldur
            const filledTemplate = this.fillTemplate(templateName, data);
            
            // Yeni pencere aç ve yazdır
            const printWindow = window.open('', '_blank', 'width=800,height=600');
            printWindow.document.write(filledTemplate);
            printWindow.document.close();
            
            // Sayfa yüklendikten sonra yazdır
            printWindow.onload = function() {
                setTimeout(() => {
                    printWindow.print();
                }, 500);
            };
            
            // Pencereyi kapat
            setTimeout(() => {
                printWindow.close();
            }, 1000);
            
        } catch (error) {
            throw error;
        }
    }

    /**
     * Stop Sale raporunu yazdırır
     * @param {Object} entry - Stop sale kaydı
     */
    async printStopSaleReport(entry) {
        const data = this.prepareStopSaleData(entry);
        await this.printReport('stopSale', data);
    }

    /**
     * Raporu PDF olarak indirir (gelecekte eklenebilir)
     * @param {string} templateName - Template adı
     * @param {Object} data - Rapor verisi
     */
    async downloadPDF(templateName, data) {
        // Bu özellik gelecekte eklenebilir
        // jsPDF veya Puppeteer kullanılabilir
    }
}

// Singleton instance
const reportManager = new ReportTemplateManager();

export default reportManager;
